/**
 * Confirm marking an order completed and optionally include review CTAs
 * in the final delivery email.
 */

import React, { useEffect, useState } from "react";
import { Modal, Checkbox, Typography, Space, Alert } from "antd";
import {
  CheckCircleOutlined,
  StarOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Text, Paragraph } = Typography;

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string} [props.orderId]
 * @param {boolean} [props.loading]
 * @param {(result: { includeReviewRequest: boolean }) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const CompleteOrderConfirmModal = ({
  open,
  orderId,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [includeReviewRequest, setIncludeReviewRequest] = useState(false);

  useEffect(() => {
    if (open) {
      setIncludeReviewRequest(false);
    }
  }, [open]);

  const handleOk = () => {
    if (onConfirm) {
      onConfirm({ includeReviewRequest });
    }
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
          <span>Mark order as completed?</span>
        </Space>
      }
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Mark completed"
      confirmLoading={loading}
      destroyOnHidden
      okButtonProps={{ "data-testid": "complete-order-confirm" }}
    >
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="Final delivery email"
        description={
          orderId
            ? `Completing ${orderId} sends the usual delivery email (invoice PDF when available).`
            : "Completing sends the usual delivery email (invoice PDF when available)."
        }
      />

      <Checkbox
        checked={includeReviewRequest}
        onChange={(e) => setIncludeReviewRequest(e.target.checked)}
        data-testid="include-review-request-checkbox"
      >
        <Space>
          <StarOutlined />
          <Text strong>Send review request?</Text>
        </Space>
      </Checkbox>
      <Paragraph type="secondary" style={{ margin: "8px 0 0 28px" }}>
        When checked, the same completion email also includes Facebook and Google
        review links (“Recommend on Facebook” / “Review Bubakii on Google”).
        Unchecked keeps the current final email with no review block.
      </Paragraph>
    </Modal>
  );
};

export default CompleteOrderConfirmModal;
